import { yupResolver } from "@hookform/resolvers/yup";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { IconButton, Stack, Typography } from "@mui/material";
import { Alert } from "Components/_Shared/Alert";
import { Modal } from "Components/_Shared/BlModal";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { useConfirmClose } from "Components/_Shared/Dialogs/useConfirmClose";
import { Empty } from "Components/_Shared/Empty";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { useCreateAnalysisIssueReport, useGetAnalysis } from "Infrastructure/ApiZfp/AnalyzerApi";
import { useUserContext } from "Infrastructure/Context/UserContext";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { type ResourcePathType, Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useId, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { type ObjectSchema, object, string } from "yup";
import { REPORT_ISSUE_DESCRIPTION_MAX_LENGTH } from "../../constants";
import { AnalysisDetailContext } from "../AnalysisDetailContext";
import { ReportIssueAttachments } from "./ReportIssueAttachments";
import { getClientEnvironment } from "./getClientEnvironment";

type ReportIssueFormType = {
  description: string;
};

const getReportIssueSchema: (t: (resourcePath: ResourcePathType, options?: any) => string) => ObjectSchema<ReportIssueFormType> = (t) => {
  return object({
    description: string()
      .trim()
      .required(t(Resources.Validation.Required))
      .max(REPORT_ISSUE_DESCRIPTION_MAX_LENGTH, t(Resources.Validation.String.Max, { count: REPORT_ISSUE_DESCRIPTION_MAX_LENGTH })),
  }).defined();
};

type Props = {
  readonly open: boolean;
  readonly onClose: () => void;
};

export const ReportIssueModal = ({ open, onClose }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { data: analysis } = useGetAnalysis(analysisId, versionId);
  const { user } = useUserContext();
  const titleId = useId();

  const [attachments, setAttachments] = useState<File[]>([]);

  const methods = useForm<ReportIssueFormType>({
    resolver: yupResolver(getReportIssueSchema(t)),
    defaultValues: { description: "" },
  });

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = methods;

  const { mutate: sendIssueReport, isPending, isSuccess, isError, reset: resetMutation } = useCreateAnalysisIssueReport();

  const description = useWatch({ control, name: "description" });
  const isFormView = !isSuccess && !isError;
  const hasContent = !!description?.trim() || attachments.length > 0;

  const closeModal = () => {
    onClose();
    resetMutation();
  };

  const discardAndClose = () => {
    resetForm();
    setAttachments([]);
    closeModal();
  };

  const { requestClose, confirmCloseDialog } = useConfirmClose({
    isDirty: isFormView && hasContent,
    // Keeps the filled-in form after an error, so reopening the modal lets the user try again.
    onClose: isError ? closeModal : discardAndClose,
  });

  const handleClose = () => {
    if (isPending) return;
    requestClose();
  };

  const onSubmit = ({ description }: ReportIssueFormType) => {
    sendIssueReport({
      analysisId,
      versionId,
      data: {
        description,
        attachments,
        pageUrl: window.location.href,
        analysisName: analysis?.name ?? "",
        analysisVersion: analysis?.versions.find((version) => version.versionId === versionId)?.version,
        reporterName: user ? `${user.firstName} ${user.lastName}` : "",
        ...getClientEnvironment(),
      },
    });
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} disableBackdropClose aria-labelledby={titleId}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex width={600} maxWidth="90vw" sx={{ boxShadow: 3, background: "white", p: Spacing.XXL, borderRadius: Radius.XXXL }}>
              <Stack spacing={Spacing.XXL} width="100%">
                <Flex justifyContent="space-between">
                  <Typography id={titleId} variant="h2">
                    {t(Resources.AnalysisDetail.ReportIssue.Title)}
                  </Typography>
                  <IconButton aria-label={t(Resources.Common.Close)} onClick={handleClose} disabled={isPending}>
                    <CloseIcon />
                  </IconButton>
                </Flex>

                {isSuccess && (
                  <Empty
                    icon={<CheckCircleOutlinedIcon sx={{ color: "success.main", fontSize: 48 }} />}
                    title={t(Resources.AnalysisDetail.ReportIssue.Sent.Title)}
                    text={t(Resources.AnalysisDetail.ReportIssue.Sent.Text)}
                    actions={<Button onClick={handleClose}>{t(Resources.Common.Close)}</Button>}
                  />
                )}

                {isError && (
                  <Empty
                    icon={<ErrorOutlineIcon sx={{ color: "error.main", fontSize: 48 }} />}
                    title={t(Resources.AnalysisDetail.ReportIssue.Error.Title)}
                    text={t(Resources.AnalysisDetail.ReportIssue.Error.Text)}
                    actions={
                      <HStack spacing={Spacing.XL}>
                        <Button color="secondary" onClick={handleClose}>
                          {t(Resources.Common.Close)}
                        </Button>
                        <Button onClick={resetMutation}>{t(Resources.AnalysisDetail.ReportIssue.Error.Retry)}</Button>
                      </HStack>
                    }
                  />
                )}

                {!isSuccess && !isError && (
                  <>
                    <Alert variant="info" text={t(Resources.AnalysisDetail.ReportIssue.Hint)} />

                    <BlFormInput
                      control={control}
                      label={t(Resources.AnalysisDetail.ReportIssue.Description)}
                      placeholder={t(Resources.AnalysisDetail.ReportIssue.DescriptionPlaceholder)}
                      name="description"
                      errors={errors}
                      disabled={isPending}
                      multiline
                      minRows={6}
                      maxRows={10}
                      characterCounter={<FormCharacterCounter characterLimit={REPORT_ISSUE_DESCRIPTION_MAX_LENGTH} name="description" />}
                    />

                    <ReportIssueAttachments files={attachments} onChange={setAttachments} disabled={isPending} />

                    <Flex justifyContent="flex-end" gap={Spacing.XL}>
                      <Button color="secondary" onClick={handleClose} disabled={isPending}>
                        {t(Resources.Common.Close)}
                      </Button>
                      <Button type="submit" loading={isPending} disabled={!!errors.description}>
                        {t(Resources.AnalysisDetail.ReportIssue.Submit)}
                      </Button>
                    </Flex>
                  </>
                )}
              </Stack>
            </Flex>
          </form>
        </FormProvider>
      </Modal>
      {confirmCloseDialog}
    </>
  );
};
